import { ApolloClient, ApolloQueryResult, NormalizedCacheObject } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import { handleQuery } from '@99designs/graph-utils';
import { ProductDetailsDocument, ProductDetailsQuery } from '../graphql/ProductDetails.generated';
import { productGroupsConfig as fixedPriceProjectProducts } from '../lib/productGroups/fixed-price-project';
import { getProductsByProductNames } from './getProductsByProductNames';
import { mapDataToProductTypeArray } from './mapDataToProductTypeArray';
import type { ContestProduct, FixedPriceProjectProduct, ProductGroupName } from '../types';

export async function getProductsByProductGroupName(
    client: ApolloClient<NormalizedCacheObject>,
    name: ProductGroupName
): Promise<ContestProduct[] | FixedPriceProjectProduct[]> {
    const singleFixedPriceProjectProduct = Object.values(fixedPriceProjectProducts).some(
        (product) => product.name === name
    );

    if (singleFixedPriceProjectProduct) {
        return getProductsByProductNames(client, [name]);
    }

    const { data, error } = await fetchProductsOfProductGroup(client, name);

    if (error) {
        return [];
    }

    if (!data || !data.products) {
        Bugsnag.notify(new Error('Feature Product details - products not found'), (event) => {
            event.context = `Product details not found for ${name}`;
        });
        return [];
    }

    if (data.products.length <= 0) {
        Bugsnag.notify(new Error('Feature Product details - empty list of products'), (event) => {
            event.context = `Product details empty for ${name}`;
        });
        return [];
    }

    return mapDataToProductTypeArray(data);
}

function fetchProductsOfProductGroup(
    client: ApolloClient<NormalizedCacheObject>,
    productGroupName: ProductGroupName
): Promise<ApolloQueryResult<ProductDetailsQuery>> {
    return handleQuery<ProductDetailsQuery>(client, {
        query: ProductDetailsDocument,
        variables: {
            input: {
                productGroupNames: [productGroupName],
            },
        },
    });
}
